/** Typed HTTP client that always includes the access token (T-831/T-832). */
import { clearSession, currentAccessToken, ensureSession } from '@/lib/auth';
import { apiBase } from '@/lib/env';

export interface ApiError extends Error {
  status: number;
  detail?: string;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await ensureSession();
  const headers: Record<string, string> = {
    authorization: `Bearer ${token}`,
  };
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  const response = await fetch(`${apiBase()}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401 && currentAccessToken() !== null) {
    // The access token was rotated under us (admin revoked, server
    // restarted with a new secret, etc.). Force re-auth one more time
    // before bubbling up. Without clearSession() ensureSession() would
    // hand back the same still-unexpired (already rejected) token.
    clearSession();
    const fresh = await ensureSession();
    headers.authorization = `Bearer ${fresh}`;
    const retry = await fetch(`${apiBase()}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return parseResponse(retry);
  }
  return parseResponse(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  if (!response.ok) {
    // statusText is an empty string over HTTP/2 — never rely on it alone.
    const err: ApiError = Object.assign(
      new Error(response.statusText || `HTTP ${response.status}`) as ApiError,
      { status: response.status },
    );
    try {
      const payload = (await response.json()) as {
        error?: { message?: string };
        detail?: string;
        message?: string;
      };
      err.detail = (payload.error && payload.error.message) || payload.detail || payload.message;
      if (err.detail) {
        err.message = err.detail;
      }
    } catch {
      // ignore
    }
    throw err;
  }
  const text = await response.text();
  if (!text) {
    return undefined as unknown as T;
  }
  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
